import deepmerge from "deepmerge";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import ReactJson from "react-json-view";
import { v4 } from "uuid";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { useProperty } from "../../components/property-provider";
import { SECTION } from "../../lib/constants";
import { FormConfiguration } from "../shared/crud/form";
import SimpleCrud, { defaultCrudMessages } from "../shared/crud/simple-crud";
import { ArrayAsString } from "../shared/widgets/arrayAsString";
import { CustomJsonEditor } from "./components/customJsonEditor";
import { EngineMultiConfigEntity } from "./domain/engineMultiConfig.entity";
import {
  createEngineMultiConfig,
  deleteEngineMultiConfig,
  getAllEngineMultiConfigs,
  getEngineV21AggregateSerializedData,
  getOneEngineMultiConfig,
  updateEngineMultiConfig,
  validateEngineV21AggregateSerializedData,
} from "./infrastructure/engineMultiConfig.api";
import schema from "./infrastructure/engineMultiConfig.schema.json";
import { engineMultiConfigMessages, tableHeaderMessages } from "./messages";
import getTableColumns from "./tableColumns";

const messages = {
  ...defaultCrudMessages,
  ...engineMultiConfigMessages,
  tableHeaders: tableHeaderMessages,
};

function EngineMultiConfigForm() {
  const intl = useIntl();
  const propertyInfo = useProperty();

  const [defaultSerialized, setDefaultSerialized] = useState<string>();

  const tableColumns = useMemo(() => getTableColumns(intl), [intl]);
  const formConfiguration: FormConfiguration<EngineMultiConfigEntity> = useMemo(
    () => ({
      __fieldOrder: ["ticker", "triggersList", "serialized", "*"],
      __useProtoStyleArrays: true,
      id: {
        default: () => v4(),
        hidden: true,
      },
      ticker: {
        messages: {
          label: messages.ticker,
        },
        readonly: isNew => !isNew,
      },
      triggersList: {
        fieldWidget: ArrayAsString,
        default: [],
        messages: {
          label: messages.triggers,
        },
      },
      serialized: {
        fieldWidget: props => (
          <CustomJsonEditor
            {...props}
            helperReferenceTitle={<FormattedMessage {...messages.jsonReference} />}
            helperReference={
              <ReactJson
                src={JSON.parse(defaultSerialized || "{}")}
                collapsed={1}
                theme="shapeshifter:inverted"
              />
            }
          />
        ),
        transform: (value: string) => JSON.parse(value),
        messages: {
          label: messages.engineMultiConfigJsonLabel,
        },
      },
    }),
    [defaultSerialized]
  );

  const validate = useCallback(
    async (entity: EngineMultiConfigEntity) => {
      const merged = deepmerge(JSON.parse(defaultSerialized || "{}"), entity.serialized);
      await validateEngineV21AggregateSerializedData(
        propertyInfo.ticker,
        propertyInfo.token,
        JSON.stringify(merged)
      );
    },
    [defaultSerialized, propertyInfo.ticker, propertyInfo.token]
  );

  const handleGetAll = useCallback(async () => {
    return getAllEngineMultiConfigs(propertyInfo.ticker, propertyInfo.token);
  }, [propertyInfo.ticker, propertyInfo.token]);

  const handleCreate = useCallback(
    async (entity: EngineMultiConfigEntity) => {
      await validate(entity);
      await createEngineMultiConfig(propertyInfo.ticker, entity, propertyInfo.token);
      return "success";
    },
    [propertyInfo.ticker, propertyInfo.token, validate]
  );

  const handleGetOne = useCallback(
    async (id: string) => {
      return getOneEngineMultiConfig(propertyInfo.ticker, id, propertyInfo.token);
    },
    [propertyInfo.ticker, propertyInfo.token]
  );

  const handleUpdate = useCallback(
    async (entity: EngineMultiConfigEntity) => {
      await validate(entity);
      await updateEngineMultiConfig(propertyInfo.ticker, entity, propertyInfo.token);
    },
    [propertyInfo.ticker, propertyInfo.token, validate]
  );

  const handleDelete = useCallback(
    (id: string) => {
      return deleteEngineMultiConfig(propertyInfo.ticker, id, propertyInfo.token);
    },
    [propertyInfo.ticker, propertyInfo.token]
  );

  useEffect(() => {
    getEngineV21AggregateSerializedData(propertyInfo.ticker, propertyInfo.token).then(data =>
      setDefaultSerialized(data)
    );
  }, [propertyInfo.ticker, propertyInfo.token]);

  if (!defaultSerialized) {
    return <div></div>;
  }

  return (
    <SimpleCrud<EngineMultiConfigEntity>
      managerName={"engineMultiConfig"}
      formConfiguration={formConfiguration}
      schema={schema}
      tableColumns={tableColumns}
      listHeight={window.innerHeight - 250}
      section={SECTION.ENGINE_MULTI_CONFIG}
      onGetAll={handleGetAll}
      onGetOne={handleGetOne}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      onCreate={handleCreate}
      hideExportButton={true}
      messages={messages}
    />
  );
}

export default withAuthorizationVerifier(EngineMultiConfigForm);
